/**
 * useModulesLibrary — 2026-06-07 wire-up for the seed/modules/ REST surface.
 *
 * Fetches the adventure-modules manifest from the briefing-room backend +
 * exposes a 3-state union (idle/loading/ready/error) per MEMORY.md rule
 * `feedback_async_then_render_needs_loading_state`. Plus an on-demand
 * detail loader for the currently-selected module (lazy — list endpoint
 * returns summaries only; full JSON loads on-click).
 *
 * Mirrors usePdfLibrary.ts pattern: injected ModulesServiceModule for
 * test stubbing + useMemo-stabilized default instance + cancelled-flag
 * for unmount safety.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ModuleManifestEntry } from '../services/host/ModulesServiceModule';
import { ModulesServiceModule } from '../services/host/ModulesServiceModule';

export type ModulesLibraryState = 'idle' | 'loading' | 'ready' | 'error';

export interface ModulesLibraryHookResult {
  state: ModulesLibraryState;
  modules: ModuleManifestEntry[];
  error: string | null;
  refresh: () => void;
  /** Currently-selected module id (null = none). */
  selectedModuleId: string | null;
  /** Full JSON of the selected module (null = not loaded). */
  selectedModuleData: Record<string, unknown> | null;
  /** State of the per-module detail fetch (independent of list state). */
  selectedModuleState: ModulesLibraryState;
  selectModule: (moduleId: string | null) => void;
}

export function useModulesLibrary(
  modulesService?: ModulesServiceModule,
): ModulesLibraryHookResult {
  const resolvedService = useMemo(
    () => modulesService ?? new ModulesServiceModule(),
    [modulesService],
  );

  // Manifest state
  const [state, setState] = useState<ModulesLibraryState>('idle');
  const [modules, setModules] = useState<ModuleManifestEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Per-module detail state
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [selectedModuleData, setSelectedModuleData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedModuleState, setSelectedModuleState] =
    useState<ModulesLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectModule = useCallback((moduleId: string | null) => {
    setSelectedModuleId(moduleId);
    if (moduleId === null) {
      setSelectedModuleData(null);
      setSelectedModuleState('idle');
    }
  }, []);

  // Manifest fetch
  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setModules(result.modules);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setModules([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  // Per-module detail fetch — fires when selectedModuleId changes
  useEffect(() => {
    if (selectedModuleId === null) {
      return;
    }
    let cancelled = false;

    setSelectedModuleState('loading');
    setSelectedModuleData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedModuleId);
        if (cancelled) return;
        if (result === null) {
          setSelectedModuleState('error');
          return;
        }
        setSelectedModuleData(result);
        setSelectedModuleState('ready');
      } catch {
        if (cancelled) return;
        setSelectedModuleState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedModuleId]);

  return {
    state,
    modules,
    error,
    refresh,
    selectedModuleId,
    selectedModuleData,
    selectedModuleState,
    selectModule,
  };
}
